import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Check, Gem, Star, Sparkles, Globe2 } from "lucide-react";
import CallbackForm from "@/components/CallbackForm";
import CallbackDialog from "@/components/CallbackDialog";

export const PRICING_PLANS = [
  {
    id: "essential",
    name: "Essential Will",
    price: "₹99",
    tagline: "No calls. No waiting. Done today.",
    features: [
      "Legally structured Will under Indian Succession Act, 1925",
      "Guided intake process",
      "Executor and guardian clauses",
      "PDF & DOCX downloads",
      "30-day edits",
    ],
    footnote:
      "The most affordable way to write a legal Will — done in one sitting, delivered instantly.",
    cta: "Create My Will – ₹99",
    ctaPath: "/will-generator",
    highlight: false,
  },
  {
    id: "lawyer_review",
    name: "Senior Lawyer-Drafted Will",
    price: "₹14,999",
    tagline: "Customised for your situation.",
    features: [
      "Fill a detailed questionnaire about your assets, family and wishes",
      "A senior lawyer calls you to understand your situation personally",
      "Your Will is drafted and reviewed by a lawyer, end to end",
      "Guidance on how to sign, witness and execute your Will correctly",
      "Help deciding the right executor and understanding their responsibilities",
      "Delivered as PDF and Word, with guidance on storing it safely",
    ],
    cta: "Request a Callback",
    highlight: true,
  },
];

export const NRI_PLAN = {
  id: "nri_will",
  name: "NRI Will",
  price: "₹19,999",
  tagline: "For your Indian assets, handled with care.",
  features: [
    "Fill a detailed questionnaire about your assets in India, family and wishes",
    "A senior lawyer calls you to understand your cross-border situation personally",
    "Your Will is drafted and reviewed by a lawyer, end to end — covering your Indian assets only",
    "Guidance on how to sign, witness and execute your Will correctly from wherever you are",
    "Help deciding the right executor in India and understanding their responsibilities",
    "Delivered as PDF and Word, with guidance on storing it safely",
  ],
  footnote:
    "This Will covers Indian assets only. For assets held outside India, please consult a local attorney in your country of residence.",
  cta: "Request a Callback",
};

const TRUST_LINE =
  "Our legal consultants are senior advocates specialising exclusively in succession, testamentary and estate planning law — ensuring every Will meets the highest legal standard.";

export default function PricingPlans({
  testIdPrefix = "pricing",
  showSecondaryCta = true,
}) {
  const [open, setOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const navigate = useNavigate();

  const openCallback = (planName) => {
    setSelectedPlan(planName);
    setOpen(true);
  };

  const handleCta = (plan) => {
    if (plan.ctaPath) {
      navigate(plan.ctaPath);
    } else {
      openCallback(plan.name);
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-plans`}>
      {/* Trust line above the pricing cards */}
      <p
        className="mx-auto mb-8 max-w-3xl text-center text-sm leading-relaxed text-[#4A5568] sm:text-[15px]"
        data-testid={`${testIdPrefix}-trust-line`}
      >
        {TRUST_LINE}
      </p>

      <div className="grid gap-6 md:grid-cols-2">
        {PRICING_PLANS.map((p) => (
          <Card
            key={p.id}
            className={`relative hover-lift overflow-hidden transition-all ${
              p.highlight
                ? "border-gold/60 shadow-[0_18px_44px_-22px_rgba(197,160,89,0.55)]"
                : "border-[#0A1128]/10 shadow-sm"
            }`}
            data-testid={`${testIdPrefix}-card-${p.id}`}
          >
            {p.highlight && (
              <>
                <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-gold/15 blur-3xl" />
                <div className="absolute right-0 top-0 flex items-center gap-1.5 rounded-bl-md bg-gold px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy">
                  <Star size={12} /> Recommended
                </div>
              </>
            )}
            <CardContent className="relative flex h-full flex-col p-8 sm:p-10">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-navy text-gold">
                  {p.highlight ? <Gem size={18} /> : <Sparkles size={18} />}
                </div>
                <div>
                  <div className="font-serif text-xl text-navy">{p.name}</div>
                  <div className="text-xs text-[#4A5568]">{p.tagline}</div>
                </div>
              </div>

              <div className="mt-8 flex items-baseline gap-2">
                <span className="font-serif text-5xl text-navy">{p.price}</span>
              </div>

              <ul className="mt-8 space-y-3">
                {p.features.map((f, i) => (
                  <li key={i} className="flex gap-3 text-sm text-[#0A1128]/90">
                    <Check size={18} className="mt-0.5 shrink-0 text-gold" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {p.footnote && (
                <p
                  className="mt-5 text-xs italic leading-relaxed text-[#4A5568]/80"
                  data-testid={`${testIdPrefix}-card-${p.id}-footnote`}
                >
                  {p.footnote}
                </p>
              )}

              <div className="mt-10 flex-1" />
              <Button
                size="lg"
                className={`w-full ${
                  p.highlight
                    ? "bg-gold text-navy hover:bg-[#E5C87A]"
                    : "bg-navy text-white hover:bg-[#1A2642]"
                }`}
                onClick={() => handleCta(p)}
                data-testid={`${testIdPrefix}-cta-${p.id}`}
              >
                {p.cta}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Also Available — NRI Will (visually secondary, single card) */}
      <div className="mt-14" data-testid={`${testIdPrefix}-also-available-section`}>
        <div className="mb-5 flex items-center gap-4">
          <div className="h-px flex-1 bg-[#0A1128]/10" />
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#4A5568]">
            Also Available
          </div>
          <div className="h-px flex-1 bg-[#0A1128]/10" />
        </div>

        <Card
          className="mx-auto max-w-2xl border-[#0A1128]/10 shadow-sm hover-lift"
          data-testid={`${testIdPrefix}-card-${NRI_PLAN.id}`}
        >
          <CardContent className="flex flex-col p-7 sm:p-9">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
                <Globe2 size={16} />
              </div>
              <div>
                <div className="font-serif text-lg text-navy">{NRI_PLAN.name}</div>
                <div className="text-xs text-[#4A5568]">{NRI_PLAN.tagline}</div>
              </div>
            </div>

            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-serif text-4xl text-navy">{NRI_PLAN.price}</span>
            </div>

            <ul className="mt-6 space-y-2.5">
              {NRI_PLAN.features.map((f, i) => (
                <li key={i} className="flex gap-3 text-sm text-[#0A1128]/90">
                  <Check size={16} className="mt-0.5 shrink-0 text-gold" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <p
              className="mt-4 text-xs italic leading-relaxed text-[#4A5568]/80"
              data-testid={`${testIdPrefix}-card-${NRI_PLAN.id}-footnote`}
            >
              {NRI_PLAN.footnote}
            </p>

            <Button
              size="lg"
              className="mt-7 w-full bg-navy text-white hover:bg-[#1A2642]"
              onClick={() => openCallback(NRI_PLAN.name)}
              data-testid={`${testIdPrefix}-cta-${NRI_PLAN.id}`}
            >
              {NRI_PLAN.cta}
            </Button>
          </CardContent>
        </Card>
      </div>

      {showSecondaryCta && (
        <div
          className="mt-10 flex flex-col items-center gap-3 rounded-md border border-[#0A1128]/10 bg-[#F9FAFB] p-6 text-center sm:flex-row sm:justify-between sm:text-left"
          data-testid={`${testIdPrefix}-callback-strip`}
        >
          <div>
            <div className="font-serif text-lg text-navy">Have questions before you proceed?</div>
            <p className="mt-1 text-sm text-[#4A5568]">
              Our team will call you back within 24 hours.
            </p>
          </div>
          <CallbackDialog
            label="Request a Callback"
            className="bg-navy text-white hover:bg-[#1A2642]"
            testId={`${testIdPrefix}-secondary-callback`}
            buttonTestId={`${testIdPrefix}-secondary-callback-btn`}
          />
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid={`${testIdPrefix}-callback-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">
              Request a Callback
            </DialogTitle>
            <DialogDescription>
              Our team will call you back within 24 hours to discuss the{" "}
              <span className="font-medium text-navy">{selectedPlan}</span> plan.
            </DialogDescription>
          </DialogHeader>
          <CallbackForm
            plan={selectedPlan}
            onSuccess={() => setOpen(false)}
            testPrefix={`${testIdPrefix}-callback`}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
